import React from 'react';
import { View, Text } from '@react-pdf/renderer';
import type { ModuleDefinition } from './moduleRegistry';
import type { MonthCell, ReportData } from '../tokens/types';
import { PdfSection, PdfMonthGrid, PdfDivider, PdfStatusBar, PdfDelayBars } from '../primitives/PdfPrimitives';
import { ScreenSection, ScreenMonthGrid, ScreenDivider, ScreenStatusBar, ScreenDelayBars } from '../primitives/ScreenPrimitives';
import { colors, coverFonts, overviewColors, ptToPx, space } from '../tokens/designTokens';

const STATUS_PALETTE: Record<MonthCell['status'], { label: string; color: string }> = {
    on_time: { label: 'On time', color: colors.pos },
    delayed: { label: 'Delayed', color: colors.warn },
    default: { label: 'Default', color: colors.neg },
    missing: { label: 'Missing', color: colors.neutralSoft },
};

const countBy = (cells: MonthCell[]): Array<{ label: string; count: number; color: string }> => {
    const buckets: Record<MonthCell['status'], number> = { on_time: 0, delayed: 0, default: 0, missing: 0 };
    cells.forEach((c) => {
        buckets[c.status] += 1;
    });
    return (['on_time', 'delayed', 'default', 'missing'] as const).map((k) => ({
        label: STATUS_PALETTE[k].label,
        count: buckets[k],
        color: STATUS_PALETTE[k].color,
    }));
};

// ── Headline card (Figma node 32:100072) ──────────────────────────────
//
// Three side-by-side cards: GSTR-1, GSTR-3B, EPF on-time filing %. Tone is
// derived from the percentage so the eye reads it instantly:
//   ≥ 95%  → green   (excellent)
//   ≥ 80%  → orange  (warn)
//   < 80%  → red     (fail)

const PF = coverFonts.pdf;
const SF = coverFonts.screen;

type ComplianceTone = 'pos' | 'warn' | 'neg' | 'neutral';

const TONE_INK: Record<ComplianceTone, string> = {
    pos: colors.pos,
    warn: colors.warn,
    neg: colors.neg,
    neutral: overviewColors.inkMuted,
};

const headlineTone = (value?: string): ComplianceTone => {
    if (!value) return 'neutral';
    const m = value.match(/(\d+(?:\.\d+)?)/);
    if (!m) return 'neutral';
    const pct = parseFloat(m[1]);
    if (!Number.isFinite(pct)) return 'neutral';
    if (pct >= 95) return 'pos';
    if (pct >= 80) return 'warn';
    return 'neg';
};

const headlineDesign = {
    gridGap: 12,
    cardPad: 11,
    cardRadius: 4,
    cardBorder: 0.5,
    labelSize: 7,
    labelTrackingEm: 0.08,
    labelGap: 5,
    // Percentage value (display font, color-coded). 16pt reads as the "headline"
    // without dominating the card — at 22pt a long string like "No EPF filing data
    // available" wrapped to three lines and broke the row.
    valueSize: 16,
    valueLineHeight: 1.1,
    // Fallback value (smaller, neutral) — used when the API returns a message
    // like "No EPF filing data available" instead of a "%" reading.
    fallbackValueSize: 8,
    fallbackValueLineHeight: 1.4,
    durationSize: 7,
    durationGap: 5,
    // Constant card height — keeps the three cards visually aligned even when
    // one returns a message and the others a clean percentage.
    minHeight: 64,
} as const;

// "100%" / "94%" / "85.5%" → percentage card. Anything else (e.g. "No EPF data
// available", undefined) → fallback card.
const isPercentageValue = (v?: string): boolean => {
    if (!v) return false;
    return /\d+(?:\.\d+)?\s*%/.test(v);
};

const PdfComplianceCard: React.FC<{ label: string; value?: string; duration?: string }> = ({ label, value, duration }) => {
    const isPct = isPercentageValue(value);
    const tone = isPct ? headlineTone(value) : 'neutral';
    const ink = TONE_INK[tone];
    return (
        <View
            style={{
                flex: 1,
                borderWidth: headlineDesign.cardBorder,
                borderColor: overviewColors.border,
                // Tone-coloured left bar — same pattern as MCA Charges cards.
                // Gives the eye a fast read on which filing is healthy/warn/red
                // without having to parse the percentage.
                borderLeftWidth: 2,
                borderLeftColor: tone === 'neutral' ? overviewColors.border : ink,
                borderRadius: headlineDesign.cardRadius,
                padding: headlineDesign.cardPad,
                backgroundColor: '#FFFFFF',
                minHeight: headlineDesign.minHeight,
            }}
            wrap={false}
        >
            <Text
                style={{
                    fontSize: headlineDesign.labelSize,
                    fontFamily: PF.sans,
                    fontWeight: 600,
                    // Card label always renders in ink (black). Only the
                    // numeric value below picks up the tone colour. Previously
                    // both label + value were tinted, which made the whole
                    // card look "warn"/"neg" and obscured the actual headline.
                    color: overviewColors.ink,
                    letterSpacing: headlineDesign.labelTrackingEm * headlineDesign.labelSize,
                    textTransform: 'uppercase',
                    marginBottom: headlineDesign.labelGap,
                }}
            >
                {label}
            </Text>
            {isPct ? (
                <Text
                    style={{
                        fontSize: headlineDesign.valueSize,
                        fontFamily: PF.display,
                        fontWeight: 500,
                        color: ink,
                        lineHeight: headlineDesign.valueLineHeight,
                    }}
                >
                    {value}
                </Text>
            ) : (
                <Text
                    style={{
                        fontSize: headlineDesign.fallbackValueSize,
                        fontFamily: PF.sans,
                        fontWeight: 500,
                        color: overviewColors.inkMuted,
                        lineHeight: headlineDesign.fallbackValueLineHeight,
                    }}
                >
                    {value ?? '—'}
                </Text>
            )}
            {duration ? (
                <Text
                    style={{
                        fontSize: headlineDesign.durationSize,
                        fontFamily: PF.mono,
                        color: overviewColors.inkMuted,
                        marginTop: headlineDesign.durationGap,
                    }}
                >
                    {duration}
                </Text>
            ) : null}
        </View>
    );
};

const PdfComplianceCardRow: React.FC<{ items: Array<{ label: string; value?: string; duration?: string }> }> = ({ items }) => (
    <View style={{ flexDirection: 'row', gap: headlineDesign.gridGap }} wrap={false}>
        {items.map((it, i) => (
            <PdfComplianceCard key={i} label={it.label} value={it.value} duration={it.duration} />
        ))}
    </View>
);

const ScreenComplianceCard: React.FC<{ label: string; value?: string; duration?: string }> = ({ label, value, duration }) => {
    const isPct = isPercentageValue(value);
    const tone = isPct ? headlineTone(value) : 'neutral';
    const ink = TONE_INK[tone];
    return (
        <div
            style={{
                flex: 1,
                border: `${ptToPx(headlineDesign.cardBorder)}px solid ${overviewColors.border}`,
                borderLeft: `2px solid ${tone === 'neutral' ? overviewColors.border : ink}`,
                borderRadius: ptToPx(headlineDesign.cardRadius),
                padding: ptToPx(headlineDesign.cardPad),
                background: '#FFFFFF',
                minHeight: ptToPx(headlineDesign.minHeight),
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <div
                style={{
                    fontSize: ptToPx(headlineDesign.labelSize),
                    fontFamily: SF.sans,
                    fontWeight: 600,
                    // Card label stays ink — only the numeric value picks up
                    // the tone colour. See PdfComplianceCard for the why.
                    color: overviewColors.ink,
                    letterSpacing: `${headlineDesign.labelTrackingEm}em`,
                    textTransform: 'uppercase',
                    marginBottom: ptToPx(headlineDesign.labelGap),
                }}
            >
                {label}
            </div>
            {isPct ? (
                <div
                    style={{
                        fontSize: ptToPx(headlineDesign.valueSize),
                        fontFamily: SF.display,
                        fontWeight: 500,
                        color: ink,
                        lineHeight: headlineDesign.valueLineHeight,
                    }}
                >
                    {value}
                </div>
            ) : (
                <div
                    style={{
                        fontSize: ptToPx(headlineDesign.fallbackValueSize),
                        fontFamily: SF.sans,
                        fontWeight: 500,
                        color: overviewColors.inkMuted,
                        lineHeight: headlineDesign.fallbackValueLineHeight,
                    }}
                >
                    {value ?? '—'}
                </div>
            )}
            {duration ? (
                <div
                    style={{
                        fontSize: ptToPx(headlineDesign.durationSize),
                        fontFamily: SF.mono,
                        color: overviewColors.inkMuted,
                        marginTop: ptToPx(headlineDesign.durationGap),
                    }}
                >
                    {duration}
                </div>
            ) : null}
        </div>
    );
};

const ScreenComplianceCardRow: React.FC<{ items: Array<{ label: string; value?: string; duration?: string }> }> = ({ items }) => (
    <div style={{ display: 'flex', gap: ptToPx(headlineDesign.gridGap) }}>
        {items.map((it, i) => (
            <ScreenComplianceCard key={i} label={it.label} value={it.value} duration={it.duration} />
        ))}
    </div>
);

const PdfCompliances: React.FC<{ data: ReportData; index: string; breakBefore?: boolean }> = ({ data, index, breakBefore }) => {
    const c = data.compliances;
    if (!c) return null;
    const groups: Array<{ title: string; primary?: string; cells: MonthCell[] }> = [
        { title: 'GSTR-1 (outward supplies)', primary: c.gstr1Pct, cells: c.gstr1 },
        { title: 'GSTR-3B (summary)', primary: c.gstr3bPct, cells: c.gstr3b },
        { title: 'EPF filings', primary: c.epfPct, cells: c.epf },
    ].filter((g) => g.cells.length > 0);

    return (
        <PdfSection kicker="Compliances" title="Statutory Compliance" index={index} breakBefore={breakBefore}>
            {c.headlineCards?.length ? (
                <PdfComplianceCardRow
                    items={c.headlineCards.slice(0, 3).map((h) => ({
                        label: h.title,
                        value: h.value,
                        duration: h.duration,
                    }))}
                />
            ) : null}
            {groups.map((g, i) => (
                <View key={g.title} style={{ marginTop: i === 0 ? 0 : space.sp8 }}>
                    {/* Vertical rhythm: sp6 between MonthGrid → StatusBar → DelayBars
                        and sp8 between successive group blocks. The earlier sp4/md
                        values read as cramped — readers couldn't tell the three
                        charts inside a group apart from the divider between groups. */}
                    {i === 0 ? <PdfDivider /> : null}
                    <PdfMonthGrid title={g.title} primary={g.primary} secondary="on time" cells={g.cells} />
                    <View style={{ marginTop: space.sp6 }}>
                        <PdfStatusBar segments={countBy(g.cells)} />
                    </View>
                    <View style={{ marginTop: space.sp6 }}>
                        <PdfDelayBars cells={g.cells} />
                    </View>
                    {i < groups.length - 1 ? <PdfDivider /> : null}
                </View>
            ))}
        </PdfSection>
    );
};

const ScreenCompliances: React.FC<{ data: ReportData; index: string }> = ({ data, index }) => {
    const c = data.compliances;
    if (!c) return null;
    const groups: Array<{ title: string; primary?: string; cells: MonthCell[] }> = [
        { title: 'GSTR-1 (outward supplies)', primary: c.gstr1Pct, cells: c.gstr1 },
        { title: 'GSTR-3B (summary)', primary: c.gstr3bPct, cells: c.gstr3b },
        { title: 'EPF filings', primary: c.epfPct, cells: c.epf },
    ].filter((g) => g.cells.length > 0);

    return (
        <ScreenSection kicker="Compliances" title="Statutory Compliance" index={index}>
            {c.headlineCards?.length ? (
                <ScreenComplianceCardRow
                    items={c.headlineCards.slice(0, 3).map((h) => ({
                        label: h.title,
                        value: h.value,
                        duration: h.duration,
                    }))}
                />
            ) : null}
            {groups.map((g, i) => (
                <div key={g.title} style={{ marginTop: i === 0 ? 0 : ptToPx(space.sp8) }}>
                    {/* Mirror PDF rhythm — sp6/sp8 in pt, scaled to px. Keeps the
                        screen preview visually aligned with the exported PDF. */}
                    {i === 0 ? <ScreenDivider /> : null}
                    <ScreenMonthGrid title={g.title} primary={g.primary} secondary="on time" cells={g.cells} />
                    <div style={{ marginTop: ptToPx(space.sp6) }}>
                        <ScreenStatusBar segments={countBy(g.cells)} />
                    </div>
                    <div style={{ marginTop: ptToPx(space.sp6) }}>
                        <ScreenDelayBars cells={g.cells} />
                    </div>
                    {i < groups.length - 1 ? <ScreenDivider /> : null}
                </div>
            ))}
        </ScreenSection>
    );
};

export const compliancesModule: ModuleDefinition = {
    meta: {
        id: 'compliances',
        title: 'Statutory Compliance',
        description: 'GSTR-1, GSTR-3B & EPF filing punctuality with on-time scoring.',
        dataSource: 'GSTR-1, GSTR-3B, EPF, Compliances Headline',
    },
    hasData: (d) => {
        const c = d.compliances;
        if (!c) return false;
        return c.gstr1.length > 0 || c.gstr3b.length > 0 || c.epf.length > 0 || (c.headlineCards?.length ?? 0) > 0;
    },
    Pdf: PdfCompliances,
    Screen: ScreenCompliances,
};
